// Shared shimmer building blocks — all built on the .shine CSS class (src/styles/_shimmer.scss).
// Page-level skeletons (src/components/skeletons/pages/*.js) compose from these instead of
// hand-rolling raw <div className="shine">s, so every skeleton in the app shares the same
// sizing/spacing conventions.

export function Bone({ w = '100%', h = 12, r = 6, style, className = '' }) {
    return (
        <div
            className={`shine ${className}`}
            style={{ width: w, height: h, borderRadius: r, flexShrink: 0, ...style }}
        />
    );
}

// One list row: optional thumbnail + 1-2 text lines + optional trailing element.
// Covers Content.js's table rows, Podcast.js's big-thumb rows, text-only rows
// (PodcastAudios/AddContentToPodcast), SavedProfiles rows, etc.
export function SkeletonRow({
    thumbnailSize = 0,
    thumbnailRadius = 8,
    titleWidth = '55%',
    subtitleWidth = '30%',
    trailingWidth = 0,
    trailingHeight = 20,
    padding = '12px 16px',
    borderBottom = true,
}) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding,
            borderBottom: borderBottom ? '1px solid #F1F5F9' : 'none',
        }}>
            {thumbnailSize > 0 && <Bone w={thumbnailSize} h={thumbnailSize} r={thumbnailRadius} />}
            <div style={{ flex: 1, minWidth: 0 }}>
                <Bone h={13} w={titleWidth} style={{ marginBottom: 6 }} />
                {subtitleWidth && <Bone h={10} w={subtitleWidth} />}
            </div>
            {trailingWidth > 0 && <Bone w={trailingWidth} h={trailingHeight} r={999} />}
        </div>
    );
}

// A fixed-size card: optional image block + title + N description lines.
// Covers Organisation/Integration project cards, WelcomePage tiles, Plans pricing cards.
export function SkeletonCardTile({ w = '100%', h = 'auto', hasImage = true, imageHeight = 96, lines = 2, padding = 14 }) {
    return (
        <div style={{ width: w, height: h, border: '1px solid #E2E8F0', borderRadius: 13, padding, background: '#FFFFFF' }}>
            {hasImage && <Bone h={imageHeight} r={10} style={{ marginBottom: 12 }} />}
            <Bone h={14} w="70%" style={{ marginBottom: 8 }} />
            {Array.from({ length: lines }).map((_, i) => (
                <Bone key={i} h={10} w={i === lines - 1 ? '50%' : '90%'} style={{ marginBottom: 4 }} />
            ))}
        </div>
    );
}

// Label + big-number tile — analytics stat rows, Billing usage.
export function SkeletonStatTile() {
    return (
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, padding: 14, background: '#FFFFFF' }}>
            <Bone h={10} w="60%" style={{ marginBottom: 8 }} />
            <Bone h={20} w="40%" />
        </div>
    );
}

// Bordered card with a title + N label/input field pairs + an optional submit button.
export function SkeletonFormCard({ title = true, fields = 4, maxWidth = 560, showButton = true }) {
    return (
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 13, padding: 20, maxWidth }}>
            {title && <Bone h={18} w="40%" style={{ marginBottom: 20 }} />}
            {Array.from({ length: fields }).map((_, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                    <Bone h={11} w={90} style={{ marginBottom: 6 }} />
                    <Bone h={36} w="100%" />
                </div>
            ))}
            {showButton && <Bone h={38} w={120} r={8} style={{ marginTop: 8 }} />}
        </div>
    );
}

export function SkeletonGrid({ cols = 3, gap = 14, children }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>
            {children}
        </div>
    );
}

export function SkeletonStack({ gap = 12, children }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
            {children}
        </div>
    );
}

// A bordered card wrapper matching the list/table containers used across the app
// (Content.js, Podcast.js, etc.) — header-less by default.
export function SkeletonListCard({ children }) {
    return (
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 13, overflow: 'hidden' }}>
            {children}
        </div>
    );
}
